import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usersService } from "@/services/usersService";
import { authService } from "@/services/authService";
import type { UserDto } from "@/types/auth";
import type { UserFilterParams } from "@/types/admin-user.dto";
import { queryKeys } from "./keys";

export function useUserProfile(enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.auth.context, "profile"],
    queryFn: () => usersService.getProfile(),
    enabled,
  });
}

export function useUpdateProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UserDto) => usersService.updateProfile(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.context });
    },
  });
}

export function useChangePasswordMutation() {
  return useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      authService.changePassword(currentPassword, newPassword),
  });
}

export function useAdminUsers(params: UserFilterParams = {}) {
  return useQuery({
    queryKey: ["admin", "users", params] as const,
    queryFn: () => usersService.getAdminUsers(params),
  });
}

export function useUpdateUserStatusMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      usersService.updateUserStatus(userId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useAdminActivateMemberMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersService.adminActivateMember(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useCustomerMemberProfile(userId: string, enabled = true) {
  return useQuery({
    queryKey: ["admin", "users", userId, "member"] as const,
    queryFn: () => usersService.getCustomerMemberProfile(userId),
    enabled: enabled && Boolean(userId),
  });
}

export function useVerifyUserKycMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      approve,
      rejectionReason,
    }: {
      userId: string;
      approve: boolean;
      rejectionReason?: string;
    }) => usersService.verifyUserKyc(userId, approve, rejectionReason),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users", userId, "member"] });
    },
  });
}
